// src/pages/AutonomoCalculatorPage.js
//
// Autónomo-first journey — same underlying CalculatorSession/useCalculatorForm
// as CalculatorPage.js, different question ordering: Business → Social
// Security → Your Spain situation (reused Step1) → Other income → Result,
// vs. IRPF's Personal → Income → Result. Same shared engines, same session
// shape (autonomo_facts is additive), deliberately different entry
// experience — see the "shared session, multiple entry points" architecture
// decision.
//
// PERSONAL FACTS: reuses Step1 directly from CalculatorPage.js rather than
// rebuilding comunidad/residency/marital_status/age/beckham_status —
// those questions and their eligibility-blocking logic already exist and
// shouldn't be duplicated. Positioned FIRST, same as the IRPF calculator —
// eligibility blocks (foral territory, non-resident, Beckham active) should
// gate the whole journey before asking business questions, not come after
// them.
//
// RESULT SCREEN: reuses the real CalculatorResult component for the IRPF
// portion — it already accepts exactly the {result, session} shape this
// hook produces — with a new autónomo-specific block composed alongside it,
// not a rebuilt result display.
//
// v1a HONEST SCOPE NOTE: "Other income" reuses the full IncomeCard
// components as-is, same as CalculatorPage.js's Step 2. The "compact
// mini-forms" UX discussed during planning was never built.
//
// SESSION LIFECYCLE (draft/publish model): same as CalculatorPage.js —
// runCalculation() persists a draft (insert first call, update every call
// after, tracked via form.sessionId in useCalculatorForm.js). Nothing is
// publicly shareable until the user explicitly publishes from the result
// sidebar. See CalculatorPage.js's header comment for the full picture.

import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  useCalculatorForm,
  CURRENT_BRACKET_YEAR,
} from '../hooks/useCalculatorForm'
import { useProductMode } from '../hooks/useProductMode'
import { INCOME_CARD_CONFIG, CATEGORY_GROUPS, createEmptyIncomeItem } from '../hooks/incomeCardConfig'
import { createEmptyAutonomoFacts } from '../hooks/autonomoFactsConfig'
import IncomeCard from './IncomeCard'
import AutonomoFactsForm from './AutonomoFactsForm'
import { Step1, Step3, SpouseIncomeGate } from './CalculatorPage'
import CalculatorResult from './CalculatorResult'
import { supabase } from '../lib/supabase'
import { fetchPartnerNameByRef } from '../lib/partnerLookup'
import { getActivePartnerRef } from '../lib/partnerAttribution'
import { useAuth } from '../context/AuthContext'
import { AnonHeader, PartnerIntroScreen } from '../components/HomeLink'
import posthog from '../lib/posthog'
import { AutonomoSummaryBlock, hasPersonalFactsFromSession } from '../components/calculator/AutonomoSummaryBlock'

const SELF_EMPLOYMENT_CARD = INCOME_CARD_CONFIG.find(c => c.income_type === 'self_employment')
const OTHER_INCOME_CARDS   = INCOME_CARD_CONFIG.filter(c => c.income_type !== 'self_employment')

const STEPS = ['personal', 'business', 'social_security', 'other_income', 'spouse_income_gate', 'spouse_other_income', 'fx_rates', 'result']
// fx_rates only appears when form.hasForeignCurrencyIncome — same
// conditional-skip pattern as CalculatorPage.js's step2 -> step3 -> step4.

// ── Personal facts step — reuses Step1 verbatim ────────────────────────────
// Mirrors CalculatorPage.js's handleStep1Next: Step1 owns its own internal
// question order (eligibility, marital_status, age, dependents) via the
// same form.currentStepIndex the hook already tracks. This wrapper only
// decides what happens when that internal flow finishes — move to the next
// screen — versus CalculatorPage.js's version, which has an 'intro' screen
// to fall back to on back-at-index-0 and this page doesn't.

function PersonalFactsStep({ form, onNext, onBack }) {
  function handleNext() {
    if (form.blockedState) return
    if (form.isLastStep) onNext()
    else form.goNext()
  }

  function handleBack() {
    if (form.currentStepIndex === 0) { if (onBack) onBack() }
    else form.goBack()
  }

  return <Step1 form={{ ...form, goNext: handleNext, goBack: handleBack }} />
}

// ── Business step ─────────────────────────────────────────────────────────

function BusinessStep({ form, onNext, onBack }) {
  const items = form.incomeItemsByType.self_employment || []
  const canContinue = items.length > 0 && items.some(i => i.business_expense_path !== null)

  // The person came here specifically for autónomo — starting with an
  // empty, collapsed "+ Add" tile means an extra click before they can do
  // anything. Auto-create one empty activity and render expanded instead.
  useEffect(() => {
    if (items.length === 0) {
      form.setIncomeItems('self_employment', [createEmptyIncomeItem(SELF_EMPLOYMENT_CARD)])
    }
  }, [])

  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">Step 2 of 5 — Your business</span>
        {onBack && <button className="triage-back-link" onClick={onBack}>← Back</button>}
      </div>

      <h2 className="triage-question">Tell us about your autónomo activity</h2>
      <p className="triage-hint">
        Revenue and expenses for your self-employment work. You can add more than one
        activity if you run separate businesses.
      </p>

      <div className="cal-income-card-grid">
        <IncomeCard
          cardConfig={SELF_EMPLOYMENT_CARD}
          items={items}
          onItemsChange={next => form.setIncomeItems('self_employment', next)}
          startExpanded={true}
        />
      </div>

      {items.length === 0 && (
        <p className="cal-income-empty-hint">Add your autónomo activity above to continue.</p>
      )}

      <button className="triage-next" onClick={onNext} disabled={!canContinue}>
        Continue →
      </button>
    </div>
  )
}

// ── Social Security step ────────────────────────────────────────────────────

function SocialSecurityStep({ form, onNext, onBack }) {
  const facts = form.autonomoFactsForm || createEmptyAutonomoFacts()
  const canContinue = !!facts.autonomo_type

  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">Step 3 of 5 — Your Social Security</span>
        <button className="triage-back-link" onClick={onBack}>← Back</button>
      </div>

      <h2 className="triage-question">A bit about your RETA status</h2>
      <p className="triage-hint">
        This determines your contribution band and whether the reduced flat-rate
        contribution can apply.
      </p>

      <AutonomoFactsForm facts={facts} onChange={form.setAutonomoFacts} />

      <button className="triage-next" onClick={onNext} disabled={!canContinue}>
        Continue →
      </button>
    </div>
  )
}

// ── Other income step ───────────────────────────────────────────────────────
// v1a: full IncomeCard grid, same as CalculatorPage.js Step 2, minus the
// self-employment card (already collected in the business step above).

// itemsForOwner / mergeOwnerItems / hasAnyItemsForOwner mirror CalculatorPage.js's
// helpers exactly — storage stays one flat array per income_type, split/merged
// by owner at render time so this step can show one person's items at a time.
function itemsForOwner(items, owner) {
  return items.filter(i => (i.owner || 'self') === owner)
}
function mergeOwnerItems(items, owner, newItemsForOwner) {
  return [...items.filter(i => (i.owner || 'self') !== owner), ...newItemsForOwner]
}
function hasAnyItemsForOwner(incomeItemsByType, owner) {
  return Object.values(incomeItemsByType).some(arr => itemsForOwner(arr, owner).length > 0)
}

function OtherIncomeStep({ form, onNext, onBack, owner = 'self' }) {
  const isSpouseStep = owner === 'spouse'
  const groupedCards = CATEGORY_GROUPS
    .map(group => ({
      group,
      cards: OTHER_INCOME_CARDS.filter(c => c.category === group.key),
    }))
    .filter(({ cards }) => cards.length > 0)

  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">
          {isSpouseStep ? "Your spouse's income" : 'Step 4 of 5 — Other income'}
        </span>
        <button className="triage-back-link" onClick={onBack}>← Back</button>
      </div>

      <h2 className="triage-question">
        {isSpouseStep ? "Add your spouse's income" : 'Do you have any other income?'}
      </h2>
      <p className="triage-hint">
        {isSpouseStep
          ? 'Add anything your spouse expects to receive this year.'
          : "Spanish tax is progressive — salary, rental, or investment income affects your " +
            "rate alongside your autónomo activity. Only add what applies to you; this step " +
            "is optional."}
      </p>

      <div className="cal-income-categories">
        {groupedCards.map(({ group, cards }) => (
          <div className="cal-income-category" key={group.key}>
            <div className="cal-income-category-header">
              <div className="cal-income-category-icon-chip">
                <i className={`ti ${group.tablerIcon}`} aria-hidden="true" />
              </div>
              <div>
                <div className="u-label-base">{group.label}</div>
                <div className="cal-income-category-desc">{group.description}</div>
              </div>
            </div>
            <div className="cal-income-card-grid">
              {cards.map(cardConfig => {
                const allItemsForType = form.incomeItemsByType[cardConfig.income_type] || []
                return (
                  <IncomeCard
                    key={cardConfig.income_type}
                    cardConfig={cardConfig}
                    items={itemsForOwner(allItemsForType, owner)}
                    onItemsChange={items =>
                      form.setIncomeItems(cardConfig.income_type, mergeOwnerItems(allItemsForType, owner, items))
                    }
                    adjustmentsForm={form.adjustmentsForm}
                    setAdjustment={form.setAdjustment}
                    owner={owner}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <button className="triage-next" onClick={onNext}>
        {!isSpouseStep && form.answers.marital_status === 'married' ? 'Continue →' : 'See my estimate →'}
      </button>
    </div>
  )
}

// ── Result step ──────────────────────────────────────────────────────────────
// Composes two things: a new, honest, unstyled-beyond-existing-classes
// autónomo summary block (RETA + Modelo 130 — nothing else models these
// numbers), and the REAL CalculatorResult component for the IRPF portion —
// not a rebuild. CalculatorResult already accepts exactly the
// {result, session} shape this hook produces.

// Moved to components/calculator/AutonomoSummaryBlock.js (ticket 069). Re-exported so
// CalculatorSavedPage and any other importer of this page keep working.
export { AutonomoSummaryBlock, hasPersonalFactsFromSession }

function AutonomoResultStep({ form, onBack, onEditFx, isAuthenticated, otherProductDone, partnerRef, partner }) {
  // DELIBERATELY PASSIVE — no calculation-triggering effect here. This
  // component previously called form.runCalculation() from its own mount
  // effect, which is exactly what CalculatorPage.js's Step4 does NOT do —
  // and that difference was the actual bug. showWideResult (in the parent)
  // includes `!form.submitting`, so triggering a calculation from THIS
  // component's mount caused: narrow render → submitting flips true →
  // showWideResult flips false → this component unmounts/remounts →
  // fresh mount → fires ANOTHER calculation → repeat. Each iteration was a
  // real network call and a real posthog.capture(), which is exactly what
  // produced the rate-limiting flood. The fix: trigger runCalculation()
  // once, from the PREVIOUS screen's "Continue" button, at the same time
  // the screen transitions to 'result' — mirroring CalculatorPage.js's
  // runAndSave() pattern exactly. This component only ever reads state.

  // Explicit publish action for authenticated users, same mechanism as
  // CalculatorPage.js's handlePublish — mints public_token + published_at
  // on the current draft via form.publishSession(). Anonymous publish
  // still goes through onSave (email capture) below.
  async function handlePublish() {
    // No isAuthenticated guard: that flag is computed once at mount and goes
    // stale when an anonymous user connects via the advisor OTP flow on a
    // draft. The real Supabase session is the source of truth; throw so
    // callers (ResultCTA's Save button) can show an error.
    const { data: { session: authSession } } = await supabase.auth.getSession()
    if (!authSession) throw new Error('Not signed in')
    const token = authSession.access_token
    await form.publishSession(token)
  }

  if (form.submitting) {
    return (
      <div className="triage-form">
        <div className="triage-form-meta">
          <span className="triage-step-label">Step 5 of 5 — Your estimate</span>
        </div>
        <div className="asmt-state asmt-state--calculating">
          <div className="app-spinner" />
          <p className="triage-hint triage-hint--centered">Calculating your estimate…</p>
        </div>
      </div>
    )
  }

  if (form.submitError) {
    return (
      <div className="triage-form">
        <div className="triage-form-meta">
          <span className="triage-step-label">Step 5 of 5 — Your estimate</span>
          <button className="triage-back-link" onClick={onBack}>← Back</button>
        </div>
        <h2 className="triage-question">Something went wrong</h2>
        <div className="triage-submit-error">{form.submitError}</div>
        <button className="triage-next" onClick={() => form.runCalculation({ partnerRef })}>Try again →</button>
      </div>
    )
  }

  if (!form.result) return null

  const hasPersonalFacts = hasPersonalFactsFromSession(form.buildSession())

  return (
    <CalculatorResult
      result={form.result}
      session={form.buildSession()}
      filingComparison={form.filingComparison}
      onBack={onBack}
      onEditFxAssumptions={form.hasForeignCurrencyIncome ? onEditFx : null}
      onSave={isAuthenticated ? null : (email) => form.saveSession(email, partnerRef)}
      isAuthenticated={isAuthenticated}
      ownerState={isAuthenticated ? 'owned' : 'public'}
      published={form.published}
      onPublish={handlePublish}
      publicToken={form.savedToken}
      sessionId={form.sessionId}
      isPartnerLinked={!!partner}
      partnerName={partner?.name}
      product="autonomo"
      otherProductDone={otherProductDone ? otherProductDone('calculator') : false}
      afterHeaderContent={
        <AutonomoSummaryBlock
          autonomoResult={form.autonomoResult}
          autonomoIrpfImpact={form.autonomoIrpfImpact}
          result={form.result}
          hasPersonalFacts={hasPersonalFacts}
        />
      }
    />
  )
}

// ── Autónomo opportunity panel — shown on the IRPF calculator's result
// screen when the user added self-employment income there but hasn't
// gone through this page's dedicated RETA/Modelo 130 flow. Lets them
// answer the two extra questions inline and get autónomo numbers without
// leaving CalculatorPage.js. Deliberately reuses runCalculation() against
// the SAME edge function — see sessionOverrides below and the comment on
// runCalculation itself.

function AutonomoOpportunityPanel({ form }) {
  const [revealed, setRevealed] = useState(false)
  const [draftFacts, setDraftFacts] = useState(() => form.autonomoFactsForm || createEmptyAutonomoFacts())
  const [calculating, setCalculating] = useState(false)
  const [calcError, setCalcError] = useState(null)

  async function handleCalculate() {
    setCalculating(true)
    setCalcError(null)
    try {
      await form.runCalculation({ sessionOverrides: { autonomo_facts: draftFacts } })
    } catch (err) {
      setCalcError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setCalculating(false)
    }
  }

  if (!revealed) {
    return (
      <div className="cal-inputs-summary cal-autonomo-opportunity">
        <p className="triage-hint">
          💡 You added autónomo income. Want your Social Security (RETA) and Modelo 130
          estimate too?
        </p>
        <button className="triage-next" onClick={() => setRevealed(true)}>Show me →</button>
      </div>
    )
  }

  return (
    <div className="cal-inputs-summary cal-autonomo-opportunity">
      <div className="cal-inputs-summary-title">A couple of quick questions</div>
      <AutonomoFactsForm
        facts={draftFacts}
        onChange={patch => setDraftFacts(prev => ({ ...prev, ...patch }))}
      />
      {calcError && <div className="triage-submit-error">{calcError}</div>}
      <button className="triage-next" onClick={handleCalculate} disabled={calculating}>
        {calculating ? 'Calculating…' : 'Calculate my autónomo numbers →'}
      </button>
    </div>
  )
}

// Exported so CalculatorSavedPage.js can apply the same gate from a
// loaded result (rather than live form state) when rendering a reloaded
// result.
//
// Checks NET income, not just presence of an item — and reads from
// classified_items (the computed result), not raw session income_items,
// because raw items don't carry a computed net_amount_eur (it's null
// pre-calculation; only classification fills it in). Any non-zero net
// counts; there's no minimum-dollar threshold — €5 of real autónomo
// income is still real autónomo income and RETA genuinely applies to it.
//
// BUG FIX: a session can have autonomo_facts set — and even a computed
// autonomoResult — without ever containing a real self-employment income
// item, or with one that nets to zero. This happens because
// AutonomoOpportunityPanel (below) was being offered on every IRPF
// calculator result page unconditionally, regardless of whether the user
// had actually added self-employment income on the income step. A user
// could click "Show me →", answer the two RETA questions, and get back a
// RETA contribution computed against a zero net-returns minimum base
// (autonomoResult.reta.reta_computable_net_returns_monthly: 0) — a
// real-looking number with nothing behind it, and an empty business card
// underneath since there was no self-employment net income to summarize.
export function hasNonZeroSelfEmploymentIncome(classifiedItems) {
  return (classifiedItems || []).some(
    i => i.income_type === 'self_employment'
      && (i.owner || 'self') !== 'spouse'
      && (i.net_amount_eur ?? 0) > 0
  )
}

// Decides which of the two to show — the summary once a result exists, the
// offer beforehand — so callers (CalculatorPage.js) only need one prop.
export function AutonomoResultSlot({ form }) {
  // No non-zero self-employment income in the computed result — don't
  // offer the RETA/Modelo 130 opportunity panel at all, and don't show a
  // summary block even if autonomo_facts/autonomoResult happen to be set
  // from a prior answer.
  if (!hasNonZeroSelfEmploymentIncome(form.result?.classified_items)) return null

  if (form.autonomoResult) {
    return (
      <AutonomoSummaryBlock
        autonomoResult={form.autonomoResult}
        autonomoIrpfImpact={form.autonomoIrpfImpact}
        result={form.result}
        hasPersonalFacts={hasPersonalFactsFromSession(form.buildSession())}
      />
    )
  }
  return <AutonomoOpportunityPanel form={form} />
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AutonomoCalculatorPage() {
  const form = useCalculatorForm()
  const location = useLocation()
  const { isAuthenticated, authLoading, otherProductDone } = useProductMode()
  const { redirectAfterSignIn } = useAuth()
  const [screen, setScreen] = useState('personal')

  // Mirrors CalculatorPage.js's partner-linked capture exactly — reads from
  // the persistent cookie (src/lib/partnerAttribution.js) rather than
  // location.search, so it survives navigation to a page whose URL no
  // longer has ?ref= in it. Same partners table lookup, resolved once on
  // mount.
  const [partner, setPartner] = useState(null)
  // Disambiguates "lookup still pending" from "lookup resolved, no matching
  // partner row" (a garbage/typo'd ?ref=). Flips true once the .then() below
  // fires, whether or not a row was found — see ticket 014-partnerintro D6.
  const [partnerChecked, setPartnerChecked] = useState(false)
  const partnerRef = getActivePartnerRef()

  useEffect(() => {
    if (!partnerRef) return
    fetchPartnerNameByRef(partnerRef)
      .then(({ data }) => {
        if (data) setPartner(data)
        setPartnerChecked(true)
      })
    // CHANGED from [] — see ticket 014-partnerintro's "Timing verification"
    // finding 3. With [], a direct first-ever /autonomo-calculator?ref=…
    // landing could permanently miss this lookup (partnerRef read null at
    // the moment this effect's closure was created); now that
    // showPartnerIntro waits on partnerChecked (D6), that would hang the
    // intro-screen spinner forever instead of just silently skipping the
    // partner-name pill.
  }, [partnerRef])

  // ── Partner-referral intro screen (ticket 014-partnerintro) ────────────
  // likelyPartnerLinked is used only to decide whether to show the loading
  // spinner below — never for attribution itself, which stays exclusively
  // cookie-based via partnerRef once authLoading is false.
  const likelyPartnerLinked = !!(
    partnerRef || new URLSearchParams(window.location.search).get('ref')
  )
  const [showPartnerIntro, setShowPartnerIntro] = useState(null) // null = undecided

  useEffect(() => {
    if (authLoading) return
    if (!partnerRef) { setShowPartnerIntro(false); return }
    if (isAuthenticated) { setShowPartnerIntro(false); return }
    if (!partnerChecked) return // still waiting on the partner-name fetch — keep the spinner up
    // partnerChecked is true now. Either a real partner resolved, or the
    // ref was garbage/unrecognized (partner still null) — in that case,
    // fall straight through to the real first question, same as if
    // partnerRef had been absent, rather than showing a broken/placeholder
    // sentence.
    setShowPartnerIntro(!!partner)
  }, [authLoading, partnerRef, isAuthenticated, partnerChecked, partner])

  // Initialize autonomo_facts the moment this page mounts — CalculatorPage.js
  // never calls this, so pure-IRPF sessions are unaffected.
  useEffect(() => {
    if (!form.autonomoFactsForm) {
      form.setAutonomoFacts(createEmptyAutonomoFacts())
    }
  }, [])

  function completeStep(step, next) {
    posthog.capture('autonomo_calculator_step_completed', { step })
    setScreen(next)
  }

  // Mirrors CalculatorPage.js's showWideResult exactly — CalculatorResult
  // is a wide, two-column layout component and must NOT be nested inside
  // the narrow triage-col wrapper used for question screens. Nesting it
  // there was the earlier width bug: the form-width page rendered narrow,
  // while CalculatorSavedPage (which never wraps CalculatorResult in
  // triage-col) rendered it correctly — confirming the wrapper was the
  // actual cause, not CalculatorResult itself.
  const showWideResult = screen === 'result' && form.result && !form.submitting && !form.submitError

  const resultStepProps = {
    form,
    onBack: () => setScreen(form.hasForeignCurrencyIncome ? 'fx_rates' : 'other_income'),
    onEditFx: () => setScreen('fx_rates'),
    isAuthenticated, otherProductDone, partnerRef, partner,
  }

  // Loading gate — only for visitors who might be partner-referred (see
  // likelyPartnerLinked above). Organic/non-referred visitors never hit
  // this and see zero behavior change.
  if (likelyPartnerLinked && showPartnerIntro === null) {
    return (
      <div className="asmt-state"><div className="app-spinner" /></div>
    )
  }

  return (
    <div className="triage-root">
      {showWideResult ? (
        <AutonomoResultStep {...resultStepProps} />
      ) : (
        <div className="triage-col">
          {/* Persistent for every anonymous view of this page — see
              AnonHeader's own comment in HomeLink.js. Previously pointed
              at /calculator, which was itself just a route to that page's
              intro screen — now deleted (see portal_start_page_plan.md),
              so this needed to change regardless of the AnonHeader
              refactor: /calculator is no longer "home" for anyone, '/' is.
              Renders ProductTag inline next to the wordmark itself now —
              the separate call this page used to make is gone. */}
          <AnonHeader show={!isAuthenticated} product="autonomo" partnerName={partner?.name} onSignInSuccess={redirectAfterSignIn} />
          {/* ── Partner-referral intro (ticket 014-partnerintro), or Personal facts ── */}
          {showPartnerIntro ? (
            <PartnerIntroScreen
              partnerName={partner?.name}
              onContinue={() => setShowPartnerIntro(false)}
            />
          ) : (
            screen === 'personal' && (
              <PersonalFactsStep
                form={form}
                onNext={() => completeStep('personal', 'business')}
              />
            )
          )}
          {screen === 'business' && (
            <BusinessStep
              form={form}
              onBack={() => setScreen('personal')}
              onNext={() => completeStep('business', 'social_security')}
            />
          )}
          {screen === 'social_security' && (
            <SocialSecurityStep
              form={form}
              onBack={() => setScreen('business')}
              onNext={() => completeStep('social_security', 'other_income')}
            />
          )}
          {screen === 'other_income' && (
            <OtherIncomeStep
              form={form}
              owner="self"
              onBack={() => setScreen('social_security')}
              onNext={() => {
                if (form.answers.marital_status === 'married') {
                  completeStep('other_income', 'spouse_income_gate')
                } else if (form.hasForeignCurrencyIncome) {
                  completeStep('other_income', 'fx_rates')
                } else {
                  completeStep('other_income', 'result'); form.runCalculation({ partnerRef })
                }
              }}
            />
          )}
          {screen === 'spouse_income_gate' && (
            <SpouseIncomeGate
              form={form}
              onBack={() => setScreen('other_income')}
              onSkip={() => {
                form.set('spouse_income_choice', 'skipped')
                if (form.hasForeignCurrencyIncome) {
                  completeStep('spouse_income_gate', 'fx_rates')
                } else {
                  completeStep('spouse_income_gate', 'result'); form.runCalculation({ partnerRef })
                }
              }}
              onAdd={() => {
                form.set('spouse_income_choice', 'added')
                completeStep('spouse_income_gate', 'spouse_other_income')
              }}
            />
          )}
          {screen === 'spouse_other_income' && (
            <OtherIncomeStep
              form={form}
              owner="spouse"
              onBack={() => setScreen('spouse_income_gate')}
              onNext={() => {
                if (form.hasForeignCurrencyIncome) completeStep('spouse_other_income', 'fx_rates')
                else { completeStep('spouse_other_income', 'result'); form.runCalculation({ partnerRef }) }
              }}
            />
          )}
          {screen === 'fx_rates' && (
            <Step3
              form={form}
              onBack={() => setScreen(
                form.answers.marital_status === 'married'
                  ? (form.answers.spouse_income_choice === 'added' ? 'spouse_other_income' : 'spouse_income_gate')
                  : 'other_income'
              )}
              onNext={() => { completeStep('fx_rates', 'result'); form.runCalculation({ partnerRef }) }}
            />
          )}
          {/* form.result exists but a fresh calculation is running (e.g. after
              editing and pressing Continue again) — show the loading/error
              states inside the narrow wrapper rather than nothing, since
              showWideResult is false until a NEW result lands. */}
          {screen === 'result' && !showWideResult && (
            <AutonomoResultStep {...resultStepProps} />
          )}
        </div>
      )}
    </div>
  )
}